/**
 * Abstraction layer that routes calls through Tauri IPC or HTTP
 * depending on the runtime environment.
 */

const IS_TAURI =
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

// --- Embed mode ---
// When running inside the embeddable widget iframe, a token from the embed
// URL authenticates API calls via the X-Embed-Token header instead of the
// operator session cookie (third-party cookies are blocked in iframes).

let embedToken: string | null = null;

export function setEmbedToken(token: string) {
  embedToken = token;
}

export function isEmbedMode(): boolean {
  return embedToken !== null;
}

function authHeaders(): Record<string, string> {
  return embedToken ? { "X-Embed-Token": embedToken } : {};
}

// --- Command routing (invoke replacement) ---

const COMMAND_MAP: Record<
  string,
  { method: string; path: string; bodyKey?: string }
> = {
  chat: { method: "POST", path: "/api/chat", bodyKey: "request" },
  chat_stream: { method: "POST", path: "/api/chat/stream", bodyKey: "request" },
  list_tools: { method: "GET", path: "/api/tools" },
  list_models: { method: "POST", path: "/api/models" },
  get_config: { method: "GET", path: "/api/config" },
  set_config: { method: "PUT", path: "/api/config", bodyKey: "config" },
  get_session: { method: "GET", path: "/api/session" },
};

/**
 * Guardrail responses (429 rate/budget, 503 busy/timeout) carry a
 * user-facing `message`; throw it flagged so the chat renders the friendly
 * copy verbatim instead of wrapping it in plumbing-error text.
 */
async function errorFrom(res: Response): Promise<Error> {
  if (res.status === 429 || res.status === 503) {
    try {
      const body = await res.json();
      if (typeof body?.message === "string") {
        const err: Error & { friendly?: boolean } = new Error(body.message);
        err.friendly = true;
        return err;
      }
    } catch {
      // fall through to the generic handling
    }
  }
  const text = await res.text().catch(() => "");
  return new Error(text || `HTTP ${res.status}`);
}

export async function command<T>(
  cmd: string,
  args?: Record<string, unknown>,
): Promise<T> {
  if (IS_TAURI) {
    const { invoke } = await import("@tauri-apps/api/core");
    return invoke<T>(cmd, args);
  }

  const route = COMMAND_MAP[cmd];
  if (!route) throw new Error(`Unknown command: ${cmd}`);

  const { method, path, bodyKey } = route;

  // get_session carries the session id as a query parameter
  const query =
    cmd === "get_session" && args?.session_id
      ? `?session_id=${encodeURIComponent(args.session_id as string)}`
      : "";

  // For list_models, send the full args object as the body
  const body =
    method === "GET"
      ? undefined
      : bodyKey
        ? JSON.stringify(args?.[bodyKey])
        : JSON.stringify(args);

  const res = await fetch(path + query, {
    method,
    headers: body
      ? { "Content-Type": "application/json", ...authHeaders() }
      : Object.keys(authHeaders()).length
        ? authHeaders()
        : undefined,
    body,
  });

  if (!res.ok) {
    if (res.status === 401) {
      // In embed mode reloading loops forever (the token is in the URL);
      // surface the error to the chat UI instead.
      if (!isEmbedMode()) window.location.reload();
      throw new Error("Session expired");
    }
    throw await errorFrom(res);
  }

  // chat_stream returns no body — streaming happens via SSE
  if (cmd === "chat_stream") return undefined as T;

  return res.json();
}

// --- Event subscription (listen replacement) ---

type UnlistenFn = () => void;
type EventHandler = (payload: unknown) => void;

// Shared event bus for web mode SSE
const webListeners = new Map<string, Set<EventHandler>>();
let activeSSE: AbortController | null = null;

function addWebListener(event: string, handler: EventHandler): UnlistenFn {
  if (!webListeners.has(event)) {
    webListeners.set(event, new Set());
  }
  webListeners.get(event)!.add(handler);
  return () => {
    webListeners.get(event)?.delete(handler);
  };
}

function dispatchWebEvent(event: string, data: unknown) {
  webListeners.get(event)?.forEach((handler) => handler(data));
}

/**
 * Subscribe to a named event. Returns a Promise<UnlistenFn> for API
 * compatibility with Tauri's listen().
 */
export async function subscribe(
  event: string,
  handler: (payload: { payload: unknown }) => void,
): Promise<UnlistenFn> {
  if (IS_TAURI) {
    const { listen } = await import("@tauri-apps/api/event");
    return listen(event, handler as Parameters<typeof listen>[1]);
  }

  // Web mode — register on the shared bus
  const wrappedHandler: EventHandler = (data) => {
    handler({ payload: data });
  };
  return addWebListener(event, wrappedHandler);
}

/**
 * Start SSE streaming for a chat request (web mode only).
 * In Tauri mode this is a no-op — the invoke('chat_stream') + listen() pattern handles it.
 */
export async function startWebStream(request: unknown): Promise<void> {
  if (IS_TAURI) return;

  // Abort any prior stream
  activeSSE?.abort();
  const controller = new AbortController();
  activeSSE = controller;

  const res = await fetch("/api/chat/stream", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(request),
    signal: controller.signal,
  });

  if (!res.ok || !res.body) {
    if (res.status === 401) {
      if (!isEmbedMode()) window.location.reload();
      throw new Error("Session expired");
    }
    throw await errorFrom(res);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Parse SSE format: "event: <name>\ndata: <json>\n\n"
      const parts = buffer.split("\n\n");
      buffer = parts.pop() ?? "";

      for (const part of parts) {
        if (!part.trim()) continue;

        let eventName = "message";
        let data = "";

        for (const line of part.split("\n")) {
          if (line.startsWith("event:")) {
            eventName = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            data = line.slice(5).trim();
          }
        }

        dispatchWebEvent(eventName, data);
      }
    }
  } catch (err) {
    if ((err as Error).name !== "AbortError") throw err;
  } finally {
    if (activeSSE === controller) activeSSE = null;
  }
}

export { IS_TAURI };
